import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe, PercentPipe } from '@angular/common';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { EvaluationService } from '@app-services/evaluation.service';
import { Permission, Role, UserInfo } from '@app/store/session/session.store';
import { EvalHistoryItemType, HistoryItemType } from '@app-model/evaluation';
import { EvalPriceChangeHistoryItem } from '@app-model/eval-price-change-history';
import { finalize } from 'rxjs/operators';

const ACCENT_FALLBACK = '#16b097';
const WARNING = '#f5882f';
const ESTIMATE = '#5d9bff';
const MUTED_FALLBACK = '#8b93a0';
const GRID_FALLBACK = 'rgba(255,255,255,0.08)';
const POINT_FILL_FALLBACK = '#ffffff';
const TOOLTIP_BG_FALLBACK = '#0f1c2c';
const TOOLTIP_TEXT_FALLBACK = '#ffffff';
const TOOLTIP_DIVIDER_FALLBACK = 'rgba(255,255,255,0.08)';
const SYMBOL_SIZE = 9;
const SYMBOL_SIZE_EMPHASIS = 14;

interface PriceChangeTooltipTheme {
  background: string;
  text: string;
  muted: string;
  divider: string;
  accent: string;
  border: string;
}

@Component({
  selector: 'app-evaluation-price-change-history',
  standalone: true,
  imports: [CommonModule, NgxEchartsDirective],
  templateUrl: './evaluation-price-change-history.component.html',
  styleUrl: './evaluation-price-change-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvaluationPriceChangeHistoryComponent implements OnInit {
  readonly vehicleId = input.required<string>();
  readonly userInfo = input.required<UserInfo>();

  private readonly evaluationService = inject(EvaluationService);
  private readonly datePipe = new DatePipe('pt-BR');
  private readonly currencyPipe = new CurrencyPipe('pt-BR');
  private readonly percentPipe = new PercentPipe('pt-BR');
  private accentColor = ACCENT_FALLBACK;
  private tooltipTheme: PriceChangeTooltipTheme = {
    background: TOOLTIP_BG_FALLBACK,
    text: TOOLTIP_TEXT_FALLBACK,
    muted: MUTED_FALLBACK,
    divider: TOOLTIP_DIVIDER_FALLBACK,
    accent: ACCENT_FALLBACK,
    border: 'rgba(22,176,151,0.45)',
  };

  readonly loading = signal(true);
  readonly items = signal<EvalPriceChangeHistoryItem[]>([]);
  readonly chartOptions = signal<EChartsOption | null>(null);

  readonly changeCount = computed(() => this.items().length);
  readonly isEmpty = computed(() => !this.loading() && this.items().length === 0);

  readonly currentPrice = computed(() => {
    const list = this.items();
    if (!list.length) {
      return null;
    }
    return list[list.length - 1].price;
  });

  readonly totalChangePercent = computed(() => {
    const list = this.items();
    if (list.length < 2) {
      return null;
    }
    const first = list[0].price;
    const last = list[list.length - 1].price;
    if (!first) {
      return null;
    }
    return ((last - first) / first) * 100;
  });

  readonly lastChange = computed(() => {
    const list = this.items();
    if (!list.length) {
      return null;
    }
    const last = list[list.length - 1];
    const date = this.datePipe.transform(last.createdAt, 'dd/MM/yyyy HH:mm');
    return `${last.userName} · ${date}`;
  });

  ngOnInit(): void {
    if (!this.canView()) {
      this.loading.set(false);
      return;
    }

    this.evaluationService
      .getPriceChangeHistory(this.vehicleId())
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (history) => {
          const sorted = [...history].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          );
          this.items.set(sorted);
          this.chartOptions.set(this.buildChartOptions(sorted));
        },
        error: () => {
          this.items.set([]);
          this.chartOptions.set(null);
        },
      });
  }

  canView(): boolean {
    const hasPermission =  this.userInfo()?.hasPermission(Permission.VIEW_EVALUATION_PRICE_HISTORY);
    const hasLegacyRoles = this.userInfo()?.hasRoles(Role.ADMIN, Role.OEM_DEALER_MANAGER);

    return hasPermission || hasLegacyRoles;
  }

  formatCurrency(value: number | null | undefined): string {
    if (value == null) {
      return '—';
    }
    return this.currencyPipe.transform(value, 'BRL', 'symbol', '1.0-0') ?? '—';
  }

  formatPercent(value: number | null | undefined): string {
    if (value == null) {
      return '—';
    }
    return this.formatSignedPercent(value / 100);
  }

  private formatSignedCurrency(value: number): string {
    const sign = value > 0 ? '+ ' : value < 0 ? '− ' : '';
    const formatted = this.currencyPipe.transform(Math.abs(value), 'BRL', 'symbol', '1.0-0') ?? '';
    return `${sign}${formatted}`;
  }

  private formatSignedPercent(ratio: number): string {
    const formatted = this.percentPipe.transform(Math.abs(ratio), '1.1-1', 'pt-BR') ?? '';
    if (ratio > 0) {
      return `+${formatted}`;
    }
    if (ratio < 0) {
      return `−${formatted}`;
    }
    return formatted;
  }

  getInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return '?';
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }

  private buildChartOptions(items: EvalPriceChangeHistoryItem[]): EChartsOption | null {
    if (!items.length) {
      return null;
    }

    this.accentColor = this.resolveCssColor('--primary-color-alt', ACCENT_FALLBACK)
      || this.resolveCssColor('--primary-color', ACCENT_FALLBACK);
    const muted = this.resolveCssColor('--on-title-muted', MUTED_FALLBACK);
    const grid = this.resolveCssColor('--on-title-grid', GRID_FALLBACK);
    const pointFill = this.resolveCssColor('--on-title-color', POINT_FILL_FALLBACK);
    this.tooltipTheme = this.resolveTooltipTheme(this.accentColor, muted, pointFill);
    const labels = items.map((item) => this.datePipe.transform(item.createdAt, 'dd/MM') ?? '');

    return {
      animationDuration: 400,
      grid: { top: 28, right: 12, bottom: 42, left: 52, containLabel: false },
      tooltip: {
        trigger: 'item',
        renderMode: 'html',
        confine: false,
        appendTo: 'body',
        className: 'price-change-history-tooltip',
        backgroundColor: this.tooltipTheme.background,
        borderColor: this.tooltipTheme.border,
        borderWidth: 1,
        padding: [10, 12],
        textStyle: { color: this.tooltipTheme.text, fontSize: 11 },
        extraCssText:
          'border-radius:8px;box-shadow:0 8px 22px rgba(0,0,0,0.4);z-index:10000;pointer-events:none;',
        position: (point, _params, _dom, _rect, size) =>
          this.getTooltipPosition(point as number[], size),
        formatter: (params) => this.buildTooltip(params.dataIndex, items),
      },
      xAxis: {
        type: 'category',
        data: labels,
        boundaryGap: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: muted,
          fontSize: 9,
          rotate: 32,
          margin: 10,
        },
      },
      yAxis: {
        type: 'value',
        splitNumber: 4,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: grid } },
        axisLabel: {
          color: muted,
          fontSize: 9,
          formatter: (value: number) => `R$ ${Number(value).toLocaleString('pt-BR')}`,
        },
      },
      series: [
        {
          type: 'line',
          smooth: false,
          cursor: 'pointer',
          lineStyle: { color: this.accentColor, width: 2 },
          emphasis: {
            focus: 'self',
            scale: true,
          },
          data: items.map((item) => {
            const marker = this.getPointMarker(item, pointFill);
            return {
              value: item.price,
              symbol: marker.symbol,
              symbolRotate: marker.symbolRotate,
              symbolSize: marker.symbolSize,
              itemStyle: marker.itemStyle,
              emphasis: marker.emphasis,
            };
          }),
        },
      ],
    };
  }

  private getTooltipPosition(
    point: number[],
    size: { contentSize: number[]; viewSize: number[] },
  ): [number, number] {
    const [px, py] = point;
    const tooltipWidth = size.contentSize[0];
    const tooltipHeight = size.contentSize[1];
    const chartHeight = size.viewSize[1];

    const showBelow = py < tooltipHeight + 20;
    const gap = 12;

    const x = px - tooltipWidth / 2;
    let y = showBelow ? py + gap : py - tooltipHeight - gap;

    if (!showBelow && y < 4) {
      y = py + gap;
    }

    if (showBelow && y + tooltipHeight > chartHeight - 4) {
      y = py - tooltipHeight - gap;
    }

    return [x, y];
  }

  private buildTooltip(index: number, items: EvalPriceChangeHistoryItem[]): string {
    const item = items[index];
    if (!item) {
      return '';
    }

    const theme = this.tooltipTheme;
    const initials = this.getInitials(item.userName);
    const datetime = this.datePipe.transform(item.createdAt, "dd/MM/yyyy 'às' HH:mm");
    const typeLabel = EvalHistoryItemType[item.type] ?? EvalHistoryItemType.UPDATED_PRICING;
    const typeStyle = this.getTypeInlineStyle(item.type);
    const deltaHtml = this.buildDeltaHtml(item);

    return [
      `<div style="min-width:168px;color:${theme.text};">`,
      `<div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">`,
      `<span style="width:24px;height:24px;border-radius:50%;background:${theme.accent};color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center">${initials}</span>`,
      `<span style="font-size:12px;font-weight:700;color:${theme.text}">${item.userName}</span>`,
      `</div>`,
      `<div style="display:flex;align-items:center;gap:6px;color:${theme.muted};font-size:11px;margin-bottom:8px">`,
      `<i class="fa-solid fa-calendar-day"></i>${datetime}`,
      `</div>`,
      `<div style="margin-bottom:8px"><span style="font-size:10px;font-weight:700;padding:2px 8px;border-radius:999px;${typeStyle}">${typeLabel}</span></div>`,
      `<div style="height:1px;background:${theme.divider};margin-bottom:8px"></div>`,
      `<div style="font-size:18px;font-weight:900;margin-bottom:5px;color:${theme.text}">${this.formatCurrency(item.price)}</div>`,
      deltaHtml,
      `</div>`,
    ].join('');
  }

  private buildDeltaHtml(item: EvalPriceChangeHistoryItem): string {
    const theme = this.tooltipTheme;
    if (item.priceVariation == null || item.priceVariationPercent == null) {
      return `<div style="font-size:11px;color:${theme.muted}"><i class="fa-solid fa-flag"></i> Primeiro valor registrado</div>`;
    }

    const isUp = item.priceVariation > 0;
    const color = isUp ? theme.accent : WARNING;
    const icon = isUp ? 'fa-arrow-trend-up' : 'fa-arrow-trend-down';
    const variationText = this.formatSignedCurrency(item.priceVariation);
    const percentText = this.formatSignedPercent(item.priceVariationPercent);

    return `<div style="font-size:11px;font-weight:700;color:${color}"><i class="fa-solid ${icon}"></i> ${variationText} · ${percentText}</div>`;
  }

  private getPointMarker(item: EvalPriceChangeHistoryItem, pointFill = POINT_FILL_FALLBACK): {
    symbol: string;
    symbolRotate?: number;
    symbolSize: number;
    itemStyle: { color: string; borderColor: string; borderWidth: number };
    emphasis: {
      scale?: boolean;
      symbolSize: number;
      itemStyle: { color: string; borderColor: string; borderWidth: number };
    };
  } {
    switch (item.type) {
      case HistoryItemType.INTERVENTION_PRICE_ACCEPTED:
        return {
          symbol: 'rect',
          symbolRotate: 0,
          symbolSize: SYMBOL_SIZE,
          itemStyle: { color: WARNING, borderColor: WARNING, borderWidth: 2 },
          emphasis: {
            scale: true,
            symbolSize: SYMBOL_SIZE_EMPHASIS,
            itemStyle: { color: WARNING, borderColor: pointFill, borderWidth: 2 },
          },
        };
      case HistoryItemType.ESTIMATED_PRICE:
        return {
          symbol: 'triangle',
          symbolRotate: 270,
          symbolSize: SYMBOL_SIZE,
          itemStyle: { color: ESTIMATE, borderColor: ESTIMATE, borderWidth: 2 },
          emphasis: {
            scale: true,
            symbolSize: SYMBOL_SIZE_EMPHASIS,
            itemStyle: { color: ESTIMATE, borderColor: pointFill, borderWidth: 2 },
          },
        };
      default:
        return {
          symbol: 'circle',
          symbolSize: SYMBOL_SIZE,
          itemStyle: { color: pointFill, borderColor: this.accentColor, borderWidth: 2 },
          emphasis: {
            scale: true,
            symbolSize: SYMBOL_SIZE_EMPHASIS,
            itemStyle: { color: this.accentColor, borderColor: this.accentColor, borderWidth: 2 },
          },
        };
    }
  }

  private resolveTooltipTheme(
    accent: string,
    muted: string,
    text: string,
  ): PriceChangeTooltipTheme {
    const background = this.resolveCssColor('--title-color', TOOLTIP_BG_FALLBACK);
    const divider = this.resolveCssColor('--on-title-divider', TOOLTIP_DIVIDER_FALLBACK);

    return {
      background,
      text: text || TOOLTIP_TEXT_FALLBACK,
      muted: muted || MUTED_FALLBACK,
      divider,
      accent,
      border: this.colorWithAlpha(accent, 0.45),
    };
  }

  private colorWithAlpha(color: string, alpha: number): string {
    const hex = color.trim();
    const short = /^#([0-9a-f]{3})$/i.exec(hex);
    const full = /^#([0-9a-f]{6})$/i.exec(hex);

    if (short) {
      const [r, g, b] = short[1].split('').map((c) => parseInt(c + c, 16));
      return `rgba(${r},${g},${b},${alpha})`;
    }

    if (full) {
      const value = full[1];
      const r = parseInt(value.slice(0, 2), 16);
      const g = parseInt(value.slice(2, 4), 16);
      const b = parseInt(value.slice(4, 6), 16);
      return `rgba(${r},${g},${b},${alpha})`;
    }

    const rgb = /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(hex);
    if (rgb) {
      return `rgba(${rgb[1]},${rgb[2]},${rgb[3]},${alpha})`;
    }

    return color;
  }

  private resolveCssColor(cssVar: string, fallback: string): string {
    const value = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
    return value || fallback;
  }

  private getTypeInlineStyle(type: HistoryItemType): string {
    switch (type) {
      case HistoryItemType.INTERVENTION_PRICE_ACCEPTED:
        return 'background:rgba(245,136,47,0.16);color:#f5882f';
      case HistoryItemType.ESTIMATED_PRICE:
        return 'background:rgba(93,155,255,0.16);color:#5d9bff';
      default: {
        const accent = this.tooltipTheme.accent;
        return `background:${this.colorWithAlpha(accent, 0.16)};color:${accent}`;
      }
    }
  }
}
